import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { tokenStorage } from '../utils/tokenStorage';
import { guestSessionManager } from '../utils/guestSession';
import { logger } from '../utils/logger';
import { refreshTokensSingleFlight } from './tokenRefresh';

const CLIENT_APP_ID = 'my-ez-topup';

// Track refresh attempts to prevent infinite loops
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let isRefreshing = false;
interface QueueItem {
  resolve: (value?: string | null) => void;
  reject: (reason?: unknown) => void;
}

let failedQueue: QueueItem[] = [];

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(async (config) => {
  config.headers['X-Client-App'] = CLIENT_APP_ID;
  // Skip auth for refresh token requests to prevent circular dependency
  if (config.url?.includes('/auth/refresh-token')) {
    return config;
  }

  const token = tokenStorage.getAccessToken() || Cookies.get('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Guest cart identity: backend reads X-Guest-Id (not the legacy `gid` header).
    const guestId = await guestSessionManager.getSession();
    if (guestId) {
      const guestHeaders = guestSessionManager.guestIdHeaders(guestId);
      config.headers['X-Guest-Id'] = guestHeaders['X-Guest-Id'];
      config.headers.gid = guestHeaders.gid;
    }
  }
  return config;
});

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor
api.interceptors.response.use(
  async (response) => {
    // Reset refresh attempts on successful response
    refreshAttempts = 0;

    // Check for gid in response data or headers
    const gidFromData = response.data?.gid;
    const gidFromHeader = response.headers['x-guest-id'] || response.headers['X-Guest-Id'];

    // Check for gid in document.cookie (since set-cookie header is not accessible)
    let gidFromCookie = null;
    if (typeof document !== 'undefined') {
      const cookieMatch = document.cookie.match(/(?:^|; )gid=([^;]*)/);
      if (cookieMatch) {
        gidFromCookie = cookieMatch[1];
      }
    }

    // Priority order: cookie > response data > header
    const backendGid = gidFromCookie || gidFromData || gidFromHeader;

    if (backendGid) {
      const currentGid = guestSessionManager.getCurrentSessionId();
      if (backendGid !== currentGid) {
        logger.log('🔄 Updating gid from backend response:', backendGid);
        logger.log(
          '🔄 Source:',
          gidFromCookie ? 'cookie' : gidFromData ? 'response data' : 'header',
        );
        guestSessionManager.setSessionId(backendGid);

        // Also sync to localStorage for persistence
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('gid', backendGid);
          logger.log('🔄 Synced gid to localStorage:', backendGid);
        }
      }
    } else {
      // If backend doesn't send gid, ensure we have a frontend generated one
      const currentGid = guestSessionManager.getCurrentSessionId();
      if (!currentGid) {
        logger.log('🔄 No backend gid and no current gid, generating new one');
        guestSessionManager.getSession(); // This will generate and save a new gid
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check for network errors
    if (
      !error.response &&
      (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK' || !navigator.onLine)
    ) {
      const networkError = new Error('Network connection lost') as Error & {
        isNetworkError: boolean;
      };
      networkError.isNetworkError = true;
      return Promise.reject(networkError);
    }

    // Skip refresh for refresh token requests to prevent infinite loops
    if (originalRequest.url?.includes('/auth/refresh-token')) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // If we've exceeded max attempts, stop retrying for now without clearing auth
      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        logger.error(`Max refresh attempts (${MAX_REFRESH_ATTEMPTS}) exceeded; deferring refresh`);
        isRefreshing = false;
        processQueue(new Error('Max refresh attempts exceeded'), null);
        return Promise.reject(new Error('Max refresh attempts exceeded'));
      }

      try {
        const refreshToken = tokenStorage.getRefreshToken() || Cookies.get('refreshToken');
        if (!refreshToken) {
          // If we're offline, don't clear auth — allow retry later
          if (!navigator.onLine) {
            logger.warn('No refresh token available while offline; deferring logout');
            isRefreshing = false;
            processQueue(new Error('Offline, cannot refresh'), null);
            return Promise.reject(new Error('Offline, cannot refresh'));
          }
          throw new Error('No refresh token available');
        }

        refreshAttempts++;
        logger.log(
          `Axios interceptor: Attempting token refresh (${refreshAttempts}/${MAX_REFRESH_ATTEMPTS})`,
        );

        // Use shared single-flight refresh orchestrator
        const { accessToken } = await refreshTokensSingleFlight();

        logger.log('Axios interceptor: Token refreshed successfully');
        refreshAttempts = 0; // Reset on success
        isRefreshing = false;
        processQueue(null, accessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        logger.error('Axios interceptor: Token refresh failed:', refreshError);
        isRefreshing = false;
        processQueue(refreshError, null);

        // Only clear auth for explicit invalid credentials (401/403). Preserve on other failures.
        const status = (refreshError as AxiosError)?.response?.status;
        const isNetworkErr =
          !(refreshError as AxiosError)?.response &&
          ((refreshError as AxiosError)?.code === 'ERR_NETWORK' || !navigator.onLine);

        if (status === 401 || status === 403) {
          logger.warn(
            'Axios interceptor: Refresh failed with 401/403; preserving tokens for graceful multi-device handling',
          );
        } else if (isNetworkErr) {
          logger.warn('Network error during refresh; preserving session for later retry');
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
